/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { Navigate } from 'react-router-dom';
// eslint-disable-next-line import/no-extraneous-dependencies
import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  Spin,
  Row,
  Col,
  Typography,
  Switch,
  Space,
  List,
  Divider,
  Button,
  InputNumber,
  Tooltip,
} from 'antd';
import debounce from 'lodash.debounce';

import { SUBSCRIPTION_STATUS } from '../../constants';
import { useGlobals } from '../../globals-context';
import { t } from '../../utils/i18n';
import createQueryConfig from '../../queries';
import useMerchantSettings from '../../hooks/use-merchant-settings';

const containerStyle = {
  display: 'flex',
  height: '100%',
  flex: '1 1 0%',
};

const containerContentStyle = {
  flex: '1 1 0%',
};

const headerContainerStyle = {
  alignItems: 'center',
  display: 'flex',
  margin: 'auto',
  marginBottom: '1.6rem',
  minHeight: '94px',
  padding: '1.6rem 2.4rem 0',
  width: '100%',
};

const overlayStyle = {
  width: '100%',
  display: 'flex',
  minHeight: '100vh',
};

const headerTitleStyle = {
  fontSize: '25px',
  fontWeight: 700,
  margin: 0,
  padding: 0,
  flex: 1,
};

function Header() {
  return (
    <header style={headerContainerStyle} slot="header">
      <h1 style={headerTitleStyle}>{t('company-settings.title')}</h1>
    </header>
  );
}

function Navigation() {
  return (
    <div slot="navigation">
      <shore-navigation>
        <shore-navigation-item
          data-url
          title={t('company-settings.nav.basic-information')}
          url="/settings/basic-information"
        />
        <shore-navigation-item
          data-url
          title={t('company-settings.nav.booking-hours')}
          url="/settings/booking-hours"
        />
        <shore-navigation-item
          data-url
          title={t('company-settings.nav.closed-dates-and-absences')}
          url="/settings/closing-and-absences"
        />
        <shore-navigation-item
          data-url
          title={t('company-settings.nav.company-profile')}
          url="/settings/company-profile"
        />
        <shore-navigation-item data-url title="Subscription" isActive />
        <shore-navigation-item
          data-url
          title={t('company-settings.nav.privacy-security')}
          url="/settings/privacy"
        />
      </shore-navigation>
    </div>
  );
}

const settingsContainerStyle = {
  height: '100%',
  position: 'relative',
  overflowY: 'auto',
};

function SMSLimitForm({ merchantId, value, updateSmsLimit }) {
  const [smsLimit, setSmsLimit] = useState(value || 0);

  function handleOnInputLimitChange(newSmsLimit) {
    const merchant = { id: merchantId };
    setSmsLimit(newSmsLimit);

    updateSmsLimit({
      merchant,
      limit_type: 'BY_LIMIT',
      additional_value: newSmsLimit,
    });
  }

  return (
    <Space direction="vertical">
      <Typography.Text>
        {t('company-settings.subscription.sms-settings.sms-limit-input')}
      </Typography.Text>
      <InputNumber
        value={smsLimit}
        min={0}
        style={{ width: 200 }}
        onChange={handleOnInputLimitChange}
      />
    </Space>
  );
}

function SMSSubscriptionOverviewList({
  locale,
  currencyCode,
  limitType,
  freeSmsCount = 0,
  additionalSmsCount = 0,
  sentSmsCount = 0,
  pricePerSmsInCents,
}) {
  const isSmsLimitTypeUnlimited = limitType === 'UNLIMITED';
  const totalSmsQuota = (freeSmsCount || 0) + (additionalSmsCount || 0);
  const pricePerSms = pricePerSmsInCents / 100;
  const totalSmsDueForPayment = Math.max(
    (sentSmsCount || 0) - (freeSmsCount || 0),
    0
  );
  const totalSmsCost = pricePerSms * totalSmsDueForPayment;

  return (
    <List itemLayout="horizontal" style={{ maxWidth: 500 }}>
      <List.Item style={{ padding: '0.8rem 0rem' }}>
        <Typography.Text>
          {t(
            'company-settings.subscription.sms-settings.sms-overview.sms-quota'
          )}
        </Typography.Text>
        <Typography.Text>
          {isSmsLimitTypeUnlimited
            ? t(
                'company-settings.subscription.sms-settings.sms-limit-type-unlimited'
              )
            : totalSmsQuota}
        </Typography.Text>
      </List.Item>
      <List.Item style={{ padding: '0.8rem 0rem' }}>
        <Typography.Text>
          {t(
            'company-settings.subscription.sms-settings.sms-overview.free-sms'
          )}
        </Typography.Text>
        <Typography.Text>{freeSmsCount}</Typography.Text>
      </List.Item>
      <List.Item style={{ padding: '0.8rem 0rem' }}>
        <Typography.Text>
          {t(
            'company-settings.subscription.sms-settings.sms-overview.total-sms-sent'
          )}
        </Typography.Text>
        <Typography.Text>{sentSmsCount}</Typography.Text>
      </List.Item>
      <List.Item style={{ padding: '0.8rem 0rem' }}>
        <Typography.Text>
          {t(
            'company-settings.subscription.sms-settings.sms-overview.cost-per-sms'
          )}
        </Typography.Text>
        <Typography.Text>
          {new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode,
          }).format(pricePerSms)}
        </Typography.Text>
      </List.Item>
      <List.Item style={{ padding: '0.8rem 0rem' }}>
        <Typography.Text strong>
          {t(
            'company-settings.subscription.sms-settings.sms-overview.total-sms-cost'
          )}
        </Typography.Text>
        <Typography.Text>
          {new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode,
          }).format(totalSmsCost)}
        </Typography.Text>
      </List.Item>
    </List>
  );
}

function SMS({ merchantId }) {
  const { queryClient, apiClient } = useGlobals();
  const { locale } = useSelector((store) => store.currentAccount);
  const { currency } = useSelector((store) => store.currentMerchant);
  const queryConfig = createQueryConfig(queryClient, apiClient);
  const {
    data: smsUsage,
    status: smsUsageStatus,
    refetch: refetchUsage,
  } = useQuery(queryConfig.smsCurrentMonthUsage(merchantId));
  const { status: subscriptionStatus, data: subscription } = useQuery(
    queryConfig.subscription({ merchantId })
  );

  const hasError = [subscriptionStatus, smsUsageStatus].includes('error');
  const isLoading =
    [subscriptionStatus, smsUsageStatus].includes('loading') ||
    [subscriptionStatus, smsUsageStatus].includes('idle');

  const [isSmsFeatureEnabled, setIsSmsFeatureEnabled] = useState(false);
  const [isSmsLimitVisible, setSmsLimitVisibility] = useState(false);

  const merchant = { id: merchantId };

  async function updateSmsLimit(payload) {
    await apiClient.smsUsage.updateLimit(payload).then(refetchUsage);
  }

  const debouncedUpdateSmsLimit = debounce(updateSmsLimit, 1000);

  function handleOnSetSMSLimitChange(checkedValue) {
    setSmsLimitVisibility(checkedValue);
  }

  async function handleOnSMSFeatureChange(checkedValue) {
    const payload = isSmsFeatureEnabled
      ? { limit_type: 'BY_LIMIT', merchant, additional_value: 0 }
      : { limit_type: 'UNLIMITED', merchant };

    await updateSmsLimit(payload).then(() => {
      setIsSmsFeatureEnabled(checkedValue);
    });
  }

  useEffect(() => {
    if (smsUsage) {
      const isFeatureActive =
        smsUsage.limit_type === 'UNLIMITED' || smsUsage.additional_value > 0;
      setIsSmsFeatureEnabled(isFeatureActive);
      setSmsLimitVisibility(
        isFeatureActive && smsUsage.limit_type !== 'UNLIMITED'
      );
    }
  }, [smsUsage]);

  useEffect(() => {
    if (!isSmsFeatureEnabled) {
      setSmsLimitVisibility(false);
    }
  }, [isSmsFeatureEnabled]);

  useEffect(() => {
    if (
      isSmsFeatureEnabled &&
      !isSmsLimitVisible &&
      smsUsage?.limit_type === 'BY_LIMIT'
    ) {
      updateSmsLimit({ limit_type: 'UNLIMITED', merchant });
    }
  }, [isSmsFeatureEnabled, isSmsLimitVisible, smsUsage]);

  if (isLoading || hasError) {
    return null;
  }

  return (
    <Col span={24}>
      <Typography.Title level={3} style={{ marginTop: 0 }}>
        {t('company-settings.subscription.sms-settings.title')}
      </Typography.Title>
      <Typography.Text
        type="secondary"
        style={{ display: 'block', maxWidth: 650 }}
      >
        {t('company-settings.subscription.sms-settings.description')}
      </Typography.Text>
      <Row style={{ marginTop: 20 }} gutter={[16, 16]}>
        <Col span={24}>
          <Space size="small" direction="horizontal" align="center">
            <Switch
              checked={isSmsFeatureEnabled}
              onChange={handleOnSMSFeatureChange}
            />
            <Typography.Text>
              {t(
                'company-settings.subscription.sms-settings.activate-unlimited-sms'
              )}
            </Typography.Text>
          </Space>
        </Col>
        <Col span={24}>
          <Space size="small" direction="horizontal" align="center">
            <Switch
              checked={isSmsLimitVisible}
              onChange={handleOnSetSMSLimitChange}
              disabled={!isSmsFeatureEnabled}
            />
            <Typography.Text {...(!isSmsFeatureEnabled && { disabled: true })}>
              {t(
                'company-settings.subscription.sms-settings.activate-sms-limit'
              )}
            </Typography.Text>
          </Space>
        </Col>
      </Row>
      {isSmsLimitVisible && (
        <Row style={{ marginTop: 20 }} gutter={[16, 16]}>
          <Col span={24}>
            <SMSLimitForm
              merchantId={merchantId}
              value={smsUsage.additional_value}
              updateSmsLimit={debouncedUpdateSmsLimit}
            />
          </Col>
        </Row>
      )}
      <Row style={{ marginTop: 20 }} gutter={[16, 16]}>
        <Col span={24}>
          <Typography.Title level={5}>
            {t('company-settings.subscription.sms-settings.sms-overview.title')}{' '}
            <Tooltip
              title={t(
                'company-settings.subscription.sms-settings.sms-overview.title-tooltip'
              )}
            >
              <QuestionCircleOutlined style={{ color: '#bdc3c7' }} />
            </Tooltip>
          </Typography.Title>
          <SMSSubscriptionOverviewList
            locale={locale}
            currencyCode={currency}
            limitType={smsUsage.limit_type}
            sentSmsCount={smsUsage.sms_sent}
            freeSmsCount={subscription.sms ? subscription.sms.sms_limit : 0}
            additionalSmsCount={smsUsage.additional_value}
            pricePerSmsInCents={
              subscription.sms ? subscription.sms.price_per_sms_in_cents : 0
            }
          />
        </Col>
      </Row>
    </Col>
  );
}

const modalBackgroundStyles = {
  position: 'fixed',
  top: '0',
  left: '0',
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)', // semi-transparent black
  zIndex: '1000', // ensure it appears above other elements
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
  outline: 'none',
};

const subscriptionPortalSessionStyle = {
  height: '40em',
  width: '34em',
  backgroundColor: 'white',
};

const portalSessionSpinStyle = (iframeLoaded) => ({
  position: 'absolute',
  display: iframeLoaded ? 'none' : 'block',
});

function SubscriptionPortalSession({ closePortal }) {
  const { isLoading: isLoadingMerchantSettings, merchantSettings } =
    useMerchantSettings();
  const { queryClient, apiClient } = useGlobals();
  const queryConfig = createQueryConfig(queryClient, apiClient);
  const { status: portalSessionsStatus, data } = useQuery(
    queryConfig.subscriptionPortalSession(merchantSettings)
  );
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
  }, []);

  if (
    isLoadingMerchantSettings ||
    ['loading', 'idle'].includes(portalSessionsStatus)
  ) {
    return null;
  }

  if (portalSessionsStatus === 'error') {
    return (
      <p> Failed to get portal session information. Please try again later. </p>
    );
  }

  function handleKeyDown(event) {
    if (event.key === 'Escape') {
      closePortal();
    }
  }

  function handleIframeLoad() {
    setIframeLoaded(true);
  }

  return (
    <div
      ref={modalRef}
      style={modalBackgroundStyles}
      onClick={closePortal}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
    >
      <Spin style={portalSessionSpinStyle(iframeLoaded)} />
      <iframe
        style={subscriptionPortalSessionStyle}
        title="Shore's subscription management portal session"
        src={data.access_url}
        onLoad={handleIframeLoad}
      />
    </div>
  );
}

function SubscriptionManagement() {
  const [isPortalSessionVisible, setPortalSessionVisibility] = useState(false);

  function handleOnClosePortalClick() {
    setPortalSessionVisibility(false);
  }

  function handleOnOpenPortalClick() {
    setPortalSessionVisibility(true);
  }

  return (
    <Col span={24}>
      <Typography.Title level={3} style={{ marginTop: 0 }}>
        {t('company-settings.subscription.manage-subscription.title')}
      </Typography.Title>
      <Typography.Text
        type="secondary"
        style={{ display: 'block', maxWidth: 650 }}
      >
        {t('company-settings.subscription.manage-subscription.description')}
      </Typography.Text>
      <Row style={{ marginTop: 20 }} gutter={[16, 16]}>
        <Col span={24}>
          <Button type="primary" size="large" onClick={handleOnOpenPortalClick}>
            {t('button.manage-subscription.title')}
          </Button>
        </Col>
      </Row>
      {isPortalSessionVisible ? (
        <SubscriptionPortalSession closePortal={handleOnClosePortalClick} />
      ) : null}
    </Col>
  );
}

function Settings({ merchantId }) {
  return (
    <div slot="content" style={settingsContainerStyle}>
      <Row>
        <SubscriptionManagement />
        <Divider />
        <SMS merchantId={merchantId} />
      </Row>
    </div>
  );
}

export function SubscriptionSettings() {
  const { isLoading: isLoadingMerchantSettings, merchantSettings } =
    useMerchantSettings();
  const { queryClient, apiClient } = useGlobals();
  const queryConfig = createQueryConfig(queryClient, apiClient);
  const { status: subscriptionQueryStatus, data: subscription } = useQuery(
    queryConfig.subscription(merchantSettings)
  );
  const hasChargebeeSubscription = subscriptionQueryStatus === 'success';
  const subscriptionPartner = subscription?.customer?.partner;

  if (
    isLoadingMerchantSettings ||
    ['loading', 'idle'].includes(subscriptionQueryStatus)
  ) {
    return null;
  }

  if (!hasChargebeeSubscription) {
    return <Navigate to="/settings/contract" />;
  }

  if (subscription.status === SUBSCRIPTION_STATUS.IN_TRIAL) {
    return <Navigate to="/subscription" />;
  }

  if (subscriptionPartner?.toLowerCase() === 'herold') {
    return <Navigate to="/settings" />;
  }

  return (
    <div style={containerStyle}>
      <div style={containerContentStyle}>
        <div>
          <shore-overlay-layout style={overlayStyle} withoutPadding showContent>
            <Header />
            <Navigation />
            <Settings merchantId={merchantSettings.merchantId} />
          </shore-overlay-layout>
        </div>
      </div>
    </div>
  );
}
